import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useIntervalOption, useWidgets } from 'modules/Dashboard/Widgets/Hooks';
import { useStudentsProgress } from './Hooks';

import { StudentsProgress } from './StudentsProgress.component';
import { StudentsProgressPlaceholder } from './StudentsProgress.placeholder';
import { WidgetSettings } from 'modules/Dashboard/Widgets/Common';
import { WidgetFilters } from './WidgetFilters';
import { MOCKED_DATA, MOCKED_DATA_WITH_STUDENT } from './StudentsProgress.mock';
import { useNonInitialEffect } from 'modules/Core/Hooks';

const STORAGE_KEY = 'students_progress_widget_filters';

const getStoredFilters = (widgetId) => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_${widgetId}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading from localStorage:', e);
  }
  return null;
};

const saveFiltersToStorage = (widgetId, filters) => {
  try {
    localStorage.setItem(`${STORAGE_KEY}_${widgetId}`, JSON.stringify(filters));
  } catch (e) {
    console.error('Error saving to localStorage:', e);
  }
};

const clearStoredFilters = (widgetId) => {
  try {
    localStorage.removeItem(`${STORAGE_KEY}_${widgetId}`);
  } catch (e) {
    console.error('Error clearing localStorage:', e);
  }
};

export const StudentsProgressContainer = ({ widget, isMock }) => {
  const storedFilters = useMemo(
    () => (widget?.id ? getStoredFilters(widget.id) : null),
    [widget?.id]
  );

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(
    storedFilters?.userId || null
  );
  const [previousUserFilter, setPreviousUserFilter] = useState(
    storedFilters?.userData || null
  );
  const [selectedTest, setSelectedTest] = useState(
    storedFilters?.testId || null
  );
  const [selectedTestName, setSelectedTestName] = useState(
    storedFilters?.testName || null
  );
  const [startDate, setStartDate] = useState(storedFilters?.startDate || null);
  const [endDate, setEndDate] = useState(storedFilters?.endDate || null);
  const [filterDays, setFilterDays] = useState(storedFilters?.days || null);
  const [searchUser, setSearchUser] = useState('');

  const { intervalOption, setIntervalOption } = useIntervalOption({
    defaultOption:
      storedFilters?.intervalOption || widget?.settings?.default_option || '7',
  });

  const { saveWidgetSettings, removeActiveWidget } = useWidgets();

  const {
    fetchStudentsProgress,
    fetchStudentsList,
    fetchStudentsProgressTests,
    clearStudentsProgress,
    studentsProgress,
    studentsProgressTests,
    students,
  } = useStudentsProgress();

  // Format users from API response
  const formattedUsers = useMemo(() => {
    const apiUsers = students?.data || [];

    if (previousUserFilter) {
      const userExists = apiUsers.find(
        (user) => user.id === previousUserFilter.id
      );

      if (!userExists) {
        return [...apiUsers, previousUserFilter];
      }
    }
    return apiUsers;
  }, [students?.data, previousUserFilter]);

  // Get the selected user for display
  const selectedStudent = useMemo(() => {
    if (!selectedUserId) return null;
    return formattedUsers.find((user) => user.id === selectedUserId) || null;
  }, [selectedUserId, formattedUsers]);

  // Progress data from API or mock
  const progressData = useMemo(() => {
    if (!selectedStudent) {
      return MOCKED_DATA;
    }

    if (studentsProgress?.data) {
      return {
        student: selectedStudent,
        performance: studentsProgress.data,
      };
    }

    return {
      student: selectedStudent,
      performance: null,
    };
  }, [selectedStudent, studentsProgress?.data]);

  // Fetch students when search changes
  useNonInitialEffect(() => {
    if (!searchUser || searchUser.length < 3) return;

    fetchStudentsList(searchUser);
  }, [searchUser]);

  // Fetch progress data when filters change
  useEffect(() => {
    if (selectedStudent && !isMock) {
      const params = {
        studentId: selectedStudent.id,
        testId: selectedTest,
      };

      // Use date range if set, otherwise use days
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      } else {
        params.days = filterDays ? +filterDays : +intervalOption;
      }

      fetchStudentsProgress(params);
    }
  }, [
    selectedStudent,
    selectedTest,
    startDate,
    endDate,
    filterDays,
    intervalOption,
    isMock,
  ]);

  // Fetch tests when filters open
  useEffect(() => {
    if (isFiltersOpen && !isMock) {
      fetchStudentsProgressTests();
    }
  }, [isFiltersOpen, isMock]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStudentsProgress();
    };
  }, []);

  // Save filters to localStorage when they change
  useEffect(() => {
    if (!widget?.id || isMock) return;

    if (selectedUserId) {
      const filtersToStore = {
        userId: selectedUserId,
        userData: selectedStudent,
        testId: selectedTest,
        testName: selectedTestName,
        startDate,
        endDate,
        days: filterDays,
        intervalOption,
      };
      saveFiltersToStorage(widget.id, filtersToStore);
    } else {
      clearStoredFilters(widget.id);
    }
  }, [
    widget?.id,
    selectedUserId,
    selectedStudent,
    selectedTest,
    selectedTestName,
    startDate,
    endDate,
    filterDays,
    intervalOption,
    isMock,
  ]);

  const handleChangeIntervalOption = (option) => {
    setIntervalOption(option);
    setFilterDays(null); // Reset filter days when interval changes
    setStartDate(null); // Clear date range when interval option is selected
    setEndDate(null);
  };

  const handleSearchUsers = (value) => {
    // Save currently selected user to preserve it in the list
    if (selectedUserId) {
      const currentSelectedUser = formattedUsers.find(
        (user) => user.id === selectedUserId
      );
      if (currentSelectedUser) {
        setPreviousUserFilter(currentSelectedUser);
      }
    }
    setSearchUser(value);
  };

  const handleSaveFilter = ({
    userId,
    testId,
    testName,
    startDate: newStartDate,
    endDate: newEndDate,
    days,
  }) => {
    setSelectedUserId(userId || null);
    setSelectedTest(testId);
    setSelectedTestName(testName || null);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setFilterDays(days);
  };

  const isLoading = studentsProgress?.loading || students?.loading;

  if (isLoading && !isFiltersOpen && selectedStudent) {
    return <StudentsProgressPlaceholder widget={widget} />;
  }

  if (isSettingsOpen) {
    return (
      <WidgetSettings
        widget={widget}
        onSave={saveWidgetSettings}
        onClose={() => setIsSettingsOpen(false)}
        onRemoveWidget={removeActiveWidget}
      />
    );
  }

  if (isFiltersOpen) {
    return (
      <WidgetFilters
        widget={widget}
        onSave={handleSaveFilter}
        defaultUserId={selectedUserId}
        defaultTestId={selectedTest}
        defaultStartDate={startDate}
        defaultEndDate={endDate}
        defaultDays={filterDays || intervalOption}
        defaultSearch={searchUser}
        isLoading={students?.loading}
        users={formattedUsers}
        tests={studentsProgressTests?.data || []}
        onClose={() => setIsFiltersOpen(false)}
        onSearchChange={handleSearchUsers}
      />
    );
  }

  // When in mock mode (sidebar preview), show with student data
  // Otherwise show based on selected student
  const displayData = isMock ? MOCKED_DATA_WITH_STUDENT : progressData;

  return (
    <StudentsProgress
      widget={widget}
      data={displayData}
      intervalOption={filterDays || intervalOption}
      startDate={startDate}
      endDate={endDate}
      selectedTestId={selectedTest}
      selectedTestName={selectedTestName}
      onChangeIntervalOption={handleChangeIntervalOption}
      onOpenSettings={() => setIsSettingsOpen(true)}
      onOpenFilter={() => setIsFiltersOpen(true)}
      hasFiltersActive={!!selectedUserId || isMock}
      isMock={isMock}
    />
  );
};

StudentsProgressContainer.propTypes = {
  widget: PropTypes.object,
  isMock: PropTypes.bool,
};
